import { API_URL } from '@/config/env';
import { conformanceApi } from '@/lib/api';
import { hasCheckpointVersionValue } from '@/lib/checkpointKeys';

import type {
  Checkpoint,
  CheckpointSystemStatus,
  SystemCheckpointConfiguration,
} from './checkpoint.types';

export const getCheckpoints = () => {
  return conformanceApi.get<{
    message: string;
    status: string;
    data: Checkpoint[];
  }>(`${API_URL}/checkpoints`);
};

export const getSystemCheckpointConfigurations = () => {
  const url = `${API_URL.replace('/orr', '')}/system/checkpoints`;
  return conformanceApi.get<{
    message: string;
    status: string;
    data: SystemCheckpointConfiguration[];
  }>(url);
};

export const createSystemCheckpointConfigurations = (
  data: SystemCheckpointConfiguration[] | SystemCheckpointConfiguration,
) => {
  const url = `${API_URL.replace('/orr', '')}/system/checkpoints`;
  return conformanceApi.post(url, data);
};

export const updateSystemCheckpointConfiguration = (data: {
  checkpointId: string;
  checkpointVersion?: string | number;
  status: CheckpointSystemStatus;
  reason: string;
  endDate?: string;
  disabledStartDate?: string;
  disabledReason?: string;
}) => {
  const urlBase = `${API_URL.replace('/orr', '')}/system/checkpoints/${encodeURIComponent(
    data.checkpointId,
  )}`;
  const hasVersion = hasCheckpointVersionValue(data.checkpointVersion);

  const versionQuery = hasVersion
    ? `?version=${encodeURIComponent(`${data.checkpointVersion}`)}`
    : '';

  const url = `${urlBase}${versionQuery}`;
  const nowIso = new Date().toISOString();

  if (data.status === 'LIVE') {
    return conformanceApi.put(url, {
      status: {
        unScored: {},
        disabled: {},
      },
    });
  }

  if (data.status === 'PREVIEW') {
    return conformanceApi.put(url, {
      status: {
        unScored: {
          reason: data.reason,
          endDate: data.endDate,
        },
        disabled: {},
      },
    });
  }

  if (data.status === 'MAINTENANCE') {
    const maintenanceReason = /-\s*maintenance\s*$/i.test(data.reason)
      ? data.reason
      : `${data.reason} - Maintenance`;

    return conformanceApi.put(url, {
      status: {
        unScored: {
          reason: maintenanceReason,
          endDate: data.endDate,
        },
        disabled: {
          reason:
            data.disabledReason ||
            'Disabling checkpoint since maintenance window has ended',
          startDate: data.disabledStartDate || data.endDate,
        },
      },
    });
  }

  return conformanceApi.put(url, {
    status: {
      unScored: {},
      disabled: {
        reason: data.reason,
        startDate: nowIso,
      },
    },
  });
};
